/**
 * TanStack Query Hook for Jobs Created All-Time KPI
 *
 * This hook provides access to the jobs created all-time KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_jobs_created_all_time_v1 view
 *
 * @module useKpiJobsCreatedAllTimeV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiJobsCreatedAllTimeV1 } from '@/hooks/database/useKpiJobsCreatedAllTimeV1';
 *
 * function JobsCreatedDisplay() {
 *   const { data, isLoading, error } = useKpiJobsCreatedAllTimeV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Jobs Created</h2>
 *       <p>Count: {data.jobs_created}</p>
 *       <p>Last Updated: {new Date(data.calculation_date).toLocaleDateString()}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiJobsCreatedAllTimeV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <KpiCard kpi={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiJobsCreatedAllTime } from '@/types/kpi.types';

/**
 * Fetches jobs created all-time KPI data from the database
 *
 * This function queries the web.v_kpi_jobs_created_all_time_v1 view and returns
 * a single row with the total jobs created count.
 *
 * @returns {Promise<KpiJobsCreatedAllTime>} KPI data object with jobs created count and metadata
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiJobsCreatedAllTime = async (): Promise<KpiJobsCreatedAllTime> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_jobs_created_all_time_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch jobs created all-time KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from jobs created all-time KPI query');
  }

  return data;
};

/**
 * React Hook for Jobs Created All-Time KPI Data
 *
 * Fetches and caches the jobs created all-time KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'jobs-created-all-time', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiJobsCreatedAllTime>} TanStack Query result object
 * @property {KpiJobsCreatedAllTime | undefined} data - The KPI data (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiJobsCreatedAllTimeV1();
 * console.log(data?.jobs_created); // e.g., 12500
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiJobsCreatedAllTimeV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <KpiDisplay value={data.jobs_created} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiJobsCreatedAllTimeV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiJobsCreatedAllTimeV1 = (): UseQueryResult<KpiJobsCreatedAllTime> => {
  return useQuery<KpiJobsCreatedAllTime>({
    queryKey: ['kpi', 'jobs-created-all-time', 'v1'],
    queryFn: fetchKpiJobsCreatedAllTime,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Jobs Created All-Time KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiJobsCreatedAllTimeQueryKey } from '@/hooks/database/useKpiJobsCreatedAllTimeV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiJobsCreatedAllTimeQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiJobsCreatedAllTimeQueryKey = (): string[] => {
  return ['kpi', 'jobs-created-all-time', 'v1'];
};

/**
 * Prefetch Function for Jobs Created All-Time KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiJobsCreatedAllTime } from '@/hooks/database/useKpiJobsCreatedAllTimeV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiJobsCreatedAllTime(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiJobsCreatedAllTime = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiJobsCreatedAllTimeQueryKey(),
    queryFn: fetchKpiJobsCreatedAllTime,
    staleTime: 3600000,
  });
};
